import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type ReadReceiptJSON = z.infer<typeof ReadReceiptSchema.ReadReceiptJSONZ>;
export type ReadReceiptParams = z.infer<typeof ReadReceiptSchema.ReadReceiptZ>;

export default class ReadReceiptSchema {
  public static readonly ReadReceiptJSONZ = z.object({
    userId: z.uuid(),
    seenAt: z.iso.datetime({ offset: true }),
  });

  public static readonly ReadReceiptZ = z.object({
    userId: z.instanceof(Guid),
    seenAt: DateSchema.type,
  });

  public static validate(values: unknown): ReadReceiptParams {
    return parseOrThrow(ReadReceiptSchema.ReadReceiptZ, values, {
      logError: true,
      schemaName: "ReadReceiptSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): ReadReceiptJSON {
    return parseOrThrow(ReadReceiptSchema.ReadReceiptJSONZ, json, {
      logError: true,
      schemaName: "ReadReceiptSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is ReadReceiptParams {
    try {
      ReadReceiptSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is ReadReceiptJSON {
    try {
      ReadReceiptSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: ReadReceiptJSON): ReadReceiptParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(ReadReceiptSchema.ReadReceiptJSONZ, json, {
      logError: true,
      schemaName: "ReadReceiptSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return ReadReceiptSchema.codec.decode(parsed);
  }

  public static serialize(params: ReadReceiptParams): ReadReceiptJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = ReadReceiptSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return ReadReceiptSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(ReadReceiptSchema.ReadReceiptJSONZ, ReadReceiptSchema.ReadReceiptZ, {
    decode: (data) => ({
      userId: Guid.fromJSON(data.userId)!,
      seenAt: new AHDate(DateSchema.deserialize(data.seenAt)),
    }),
    encode: (entity) => ({
      userId: entity.userId.toJSON(),
      seenAt: entity.seenAt.toJSON(),
    }),
  });
}
